import type { TRouteMap } from '@semios/app-platform-banyan-route-definitions'
import { omit } from 'lodash'
import md5 from 'md5'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import { networkStore } from '../App/ServiceCenter/store/networkStore'
import { errorCodes, FailedRequestReason, OfflineRequestError } from './errorCodes'
import { getNetworkStatus } from './networkStatus/getNetworkStatus'
import type { TResponseCacheEntry } from './responseCache/_types'
import { getResponseCacheEntry } from './responseCache/getResponseCacheEntry'
import { makeResponseCacheKey } from './responseCache/responseCacheKey'
import { setResponseCacheEntry } from './responseCache/setResponseCacheEntry'

const DEFAULT_TIMEOUT = 30000 // 30 seconds

/**
 *
 * @param {string} url - the url of the post request on our API
 * @param {object} body - payload body for the request
 * @param {object} params - optional additional arguments for the request (method, headers, etc.)
 * @returns {Promise<object>} a promise of the API response
 */

export const apiFetch = async <T extends keyof TRouteMap>({
  url,
  body,
  params,
}: {
  url: T
  body: TRouteMap[T]['Request']
  params?: Omit<RequestInit, 'body'> & { timeout?: number; useCache?: boolean }
}): Promise<TRouteMap[T]['Response']> => {
  let serializedBody: string | object | undefined | null = null
  let requestHash: string | null = null
  let cachedResponse: TResponseCacheEntry<TRouteMap[T]['Response']> | null = null

  if (body) {
    if (typeof body === 'string' || body instanceof FormData) {
      serializedBody = body
    } else {
      serializedBody = JSON.stringify(body)
    }
  }

  if (params?.useCache) {
    requestHash = md5(JSON.stringify({ url, body, params }))

    const connectionStatus = networkStore.selectors.getConnectionStatus(networkStore.getState())

    cachedResponse = await getResponseCacheEntry<TRouteMap[T]['Response']>(makeResponseCacheKey(requestHash))

    // If app is offline and we have a cached response, return the cached response
    if (!connectionStatus.connected && cachedResponse) return cachedResponse.response

    if (cachedResponse?.hash) {
      params.headers = {
        ...params.headers,
        // If cached response, send the content hash to the server to check if the content has changed
        'Content-Digest': cachedResponse?.hash,
      }
    } else {
      params.headers = {
        ...params.headers,
        // If no cached response, request the content hash from the server
        'Want-Content-Digest': 'md5=1',
      }
    }
  }

  const { timeout = DEFAULT_TIMEOUT } = params || {}
  const controller = new AbortController()
  const timeoutRef = setTimeout(() => controller.abort(), timeout)

  const requestOptions = {
    headers: {
      'Content-Type': body === null ? 'null' : 'application/json',
      ...(await hubConnectorSettings.getAuthHeader()),
      'app-name': hubConnectorSettings.apiFetchHeaderAppName,
      'Accept-Encoding': 'gzip',
      'referer-url': window.location.href,
      ...params?.headers,
    },
    method: params?.method || 'POST',
    body: serializedBody,
    ...omit(params, 'headers', 'method'),
    signal: controller.signal,
  } as RequestInit

  const urlToUse = `${hubConnectorSettings.apiUrl}${`/${url}`.replace(/\/\/+/g, '/')}` // add a regex to delete extra slashes

  //The promise returned from fetch() won't reject on HTTP errors even if the response is an HTTP 404 or 500. Instead,
  //as soon as the server responds with headers, the promise will resolve (with the ok property of the response set to
  //false if the response isn't in the range 200–299). The promise will only reject on network failure or if anything
  //prevented the request from completing.
  return fetch(urlToUse, requestOptions)
    .then(async (res: Response) => {
      if (params?.useCache) {
        return handleCachedResponse(res, requestHash, cachedResponse)
      } else {
        // If no cache, handle response normally
        return errorCodes(res)
      }
    })
    .catch(async (error) => {
      if (controller.signal.aborted) {
        throw new OfflineRequestError(FailedRequestReason.TIMEOUT)
      } else {
        const { connected } = await getNetworkStatus()

        if (!connected) {
          throw new OfflineRequestError(FailedRequestReason.OFFLINE)
        } else {
          throw error
        }
      }
    })
    .finally(() => clearTimeout(timeoutRef))
}

export const handleCachedResponse = async (
  res: Response,
  requestHash: string | null,
  cachedResponse: TResponseCacheEntry | null,
) => {
  const { status } = res

  // Only use cache for successful responses
  if (status >= 200 && status <= 399) {
    if (requestHash === null) return errorCodes(res)

    const cacheKey = makeResponseCacheKey(requestHash)
    const contentHash = res.headers.get('content-digest')

    // the server answers 204 with no body when the hash it was sent still matches, so return that entry
    // rather than reading storage again
    if (cachedResponse && contentHash === cachedResponse.hash) {
      return cachedResponse.response
    } else {
      const content = await res.json()

      // Not awaited: the write never rejects, and a slow or failed one only costs a later cache miss
      setResponseCacheEntry(cacheKey, {
        response: content,
        hash: contentHash,
      })

      return content
    }
  }

  // Handle error codes
  return errorCodes(res)
}

export const handleOfflineError = async (f: () => Promise<void>) => {
  await f().catch((error: Error) => {
    if (error instanceof OfflineRequestError) {
      // eslint-disable-next-line no-console
      console.warn('Offline request error', error.stack)
    } else {
      throw error
    }
  })
}
